// 03 / ACCESSORIES — rule-aware list. Visibility, default-on, and "Included"
// state all derive from rules.ts. Customer toggles fire blocks (e.g. picking
// Hydraulics auto-removes Casters).

import { useMemo, useState } from "react";

import { SectionFrame } from "./SectionFrame";
import { OptionRow } from "./OptionRow";
import { ProductDetailModal } from "../ProductDetailModal";
import { useConfigurator } from "../../store";
import type { Product } from "../../api";
import {
  effectivePriceCents,
  ID,
  isAccessoryVisible,
  isProductIncluded,
  RULES,
  strikethroughOriginalCents,
} from "../../rules";
import { displayNameFor, thumbCandidatesFor } from "../../format";

export function AccessoriesSection() {
  const { state, dispatch } = useConfigurator();
  const catalog = state.catalog;
  const [detail, setDetail] = useState<Product | null>(null);

  const ctx = useMemo(
    () =>
      catalog && {
        catalog,
        selectedMachineId: state.selectedMachineId,
        selectedAccessoryIds: state.selectedAccessoryIds,
        selectedTableId: state.selectedTableId,
        existingSystemAnswers: state.existingSystemAnswers,
        preloved: Boolean(state.preloved),
        prelovedCategory: state.preloved?.category ?? null,
      },
    [
      catalog,
      state.selectedMachineId,
      state.selectedAccessoryIds,
      state.selectedTableId,
      state.existingSystemAnswers,
    ],
  );

  const visibleAccessories = useMemo(() => {
    if (!catalog || !ctx) return [];
    return catalog.accessories.filter((a) => isAccessoryVisible(a.id, ctx));
  }, [catalog, ctx]);

  if (!catalog || !ctx || visibleAccessories.length === 0) return null;

  const selectedCount = state.selectedAccessoryIds.filter((id) =>
    visibleAccessories.some((a) => a.id === id),
  ).length;

  return (
    <SectionFrame
      number={3}
      label="Accessories"
      title="Add accessories"
      meta={selectedCount > 0 ? `${selectedCount} selected` : "Optional"}
    >
      <div className="flex flex-col">
        {visibleAccessories.map((a) => {
          const included = isProductIncluded(a.id, ctx);
          const rule = RULES[a.id];
          const hint = included
            ? includedHint(a.id, ctx.selectedAccessoryIds)
            : rule?.blocks?.includes(ID.CASTERS) &&
                state.selectedAccessoryIds.includes(a.id) &&
                a.id === ID.HYDRAULICS
              ? "Includes casters — separate Casters not needed."
              : undefined;
          const effective = effectivePriceCents(a.id, a.price_cents, ctx);
          const strikethrough = strikethroughOriginalCents(
            a.id,
            a.price_cents,
            ctx,
          );
          return (
            <OptionRow
              key={a.id}
              name={displayNameFor(a)}
              priceCents={effective}
              // Accessories show cash only (no priceLabel override) — the
              // monthly framing lives on the machine + the Summary total.
              imageCandidates={thumbCandidatesFor(a)}
              selected={
                included || state.selectedAccessoryIds.includes(a.id)
              }
              onClick={() => dispatch({ type: "accessory/toggle", id: a.id })}
              inputType="checkbox"
              included={included}
              hint={hint}
              strikethroughOriginalCents={strikethrough ?? undefined}
              onThumbClick={() => setDetail(a)}
              // Click anywhere on the row body → open the detail
              // modal; only the checkbox itself toggles the accessory.
              detailOnRowClick
            />
          );
        })}
      </div>
      <ProductDetailModal product={detail} onClose={() => setDetail(null)} />
    </SectionFrame>
  );
}

/** Plain-language hint for an "Included" accessory. */
function includedHint(productId: string, selectedAccessoryIds: string[]): string {
  if (productId === ID.VIVID_LIGHT) {
    return "Included on Ascend.";
  }
  if (productId === ID.BOBBIN_WINDER) {
    return "Included on Startup Kit.";
  }
  if (productId === ID.CPU_MOUNT_ONLY || productId === ID.MONITOR_MOUNT_ONLY) {
    return "Included in the CPU + Monitor Mount Kit.";
  }
  // Fall-back — surface which accessory it's bundled into.
  if (selectedAccessoryIds.includes(ID.STARTUP_KIT)) return "Included in your kit.";
  return "Included.";
}
